export const profile = {
  name: "Utsav Suthar",
  firstName: "Utsav",
  role: "Software Engineering Team Leader",
  company: "Mocha Technologies",
  companyRole: "Software Engineering Team Leader",
  location: "Bhuj, India · Remote",
  yearsExperience: "3+",
  availability: "Open to select opportunities",
  email: "utsavsuthar24@gmail.com",
  github: "https://github.com/SutharUtsav",
  linkedin: "https://www.linkedin.com/in/utsavsuthar/",
  resume:
    "https://drive.google.com/file/d/1FwBaXO7CDYVXJDrvYhyScB5KOd7tsToA/view",
  hero: {
    headlineTop: "Building scalable software",
    headlineBottom: "that powers businesses.",
    subtext:
      "I lead backend security and identity for multi-tenant SaaS — designing the SSO, RBAC, and payments infrastructure that multiple products depend on. Currently Software Engineering Team Leader at Mocha Technologies, leading a team of seven while staying hands-on in the code.",
  },
  about: [
    "I'm a software engineering team leader who treats architecture — especially identity and authorization — as a business decision, not an afterthought. My work sits where security, reliability, and scale meet real revenue: company-wide SSO and RBAC, multi-tenant SaaS, and the payment and billing systems that keep products running.",
    "At Mocha Technologies I lead a seven-engineer team and own the design of a centralized Identity Center and a shared enterprise RBAC framework used across multiple products — role hierarchy, resource-level permissions, multi-tenant authorization, and access governance. I care about the details that don't show up in a demo but decide whether a platform survives its second year.",
    "I write code that's simple to read, straightforward to extend, and honest about its trade-offs. Good engineering, to me, is the quiet kind: systems that keep working — and stay secure — while the business grows around them.",
  ],
};

export const metrics = [
  {
    value: "1,000+",
    label: "Tenant organizations",
    detail: "Multi-tenant SaaS served at 100% uptime",
  },
  {
    value: "10K+",
    label: "API requests / day",
    detail: "Microservices tuned for throughput",
  },
  {
    value: "30%",
    label: "Lower p95 latency",
    detail: "Datadog-instrumented service optimization",
  },
  {
    value: "50%",
    label: "Faster deployments",
    detail: "Zero-downtime CI/CD on AWS",
  },
  {
    value: "7",
    label: "Engineers led",
    detail: "Tech lead — still hands-on in the code",
  },
  {
    value: "100%",
    label: "Uptime",
    detail: "Mocha Accounting billing & payments",
  },
];

export const experience = [
  {
    company: "Mocha Technologies",
    role: "Software Engineering Team Leader",
    period: "Apr 2025 — Present",
    location: "Remote",
    current: true,
    summary:
      "Tech lead for a 7-engineer team, owning backend security and identity across multiple Mocha products while staying hands-on.",
    problem:
      "Multiple products were each reinventing authentication and permissions — a security risk and a duplication of effort. The company needed one identity and authorization backbone that every product could trust.",
    approach: [
      "Architected a centralized Identity Center — SSO, authentication, token management, and cross-product access management (IAM) — used across products.",
      "Built a shared enterprise RBAC framework: role hierarchy, resource-level permissions, multi-tenant authorization, permission inheritance, and access governance.",
      "Designed Organizational Hierarchy Management and Payments & Invoicing for Mocha Manage (multi-tenant property management), with pricing-plan-based feature entitlement.",
      "Improved cross-product UX via a Multi-SPA integration pattern and built OpenSearch dashboards tracking signup funnels and feature usage.",
      "Instrumented microservices with Datadog (p95 latency −30% at 10K+ daily requests) and automated CI/CD with CodePipeline, CodeBuild, Docker, and CloudFormation for zero-downtime releases.",
    ],
    impact:
      "Standardized identity and authorization across the product suite, cut deployment time by 50%, and grew into the tech lead role for a team of 7.",
    stack: [
      "TypeScript",
      "Node.js",
      "Laravel",
      "SSO / IAM",
      "RBAC",
      "AWS",
      "Docker",
      "CloudFormation",
      "Datadog",
      "OpenSearch",
      "PostgreSQL",
      "Redis",
      "Prisma",
    ],
  },
  {
    company: "Mocha Technologies",
    role: "Software Engineer I",
    period: "Jul 2024 — Apr 2025",
    location: "Remote",
    current: false,
    summary:
      "Owned billing, subscriptions/entitlements, and payment processing for Mocha Accounting, a multi-tenant SaaS platform.",
    problem:
      "A multi-tenant accounting platform serving 1,000+ tenant organizations needed reliable billing, entitlement, and payment processing — with zero tolerance for downtime on money-movement paths.",
    approach: [
      "Owned end-to-end Billing & Invoicing, Subscription/Entitlement Management, and Payment Processing.",
      "Implemented real-time automatic stock tracking and recurring-transaction automation via reusable templates.",
      "Built automated overdue-invoice reminders and comparative period-over-period financial reporting.",
      "Integrated REST APIs with React frontends, reducing frontend bugs by 30% and improving data-sync reliability.",
    ],
    impact:
      "Served 1,000+ tenant organizations at 100% uptime and streamlined core accounting workflows for every tenant.",
    stack: [
      "PHP",
      "Laravel",
      "TypeScript",
      "React",
      "REST APIs",
      "MySQL",
      "PostgreSQL",
      "Redis",
    ],
  },
  {
    company: "Confidosoft Solutions Pvt Ltd",
    role: "Junior Software Engineer",
    period: "May 2023 — Jun 2024",
    location: "Vadodara, India",
    current: false,
    summary:
      "Built integration connectors and data-migration automations for a low-code integration platform.",
    problem:
      "A low-code integration platform needed reliable, reusable connectors and automated data migration to remove error-prone manual data entry.",
    approach: [
      "Built five reusable connectors — MySQL, Redis, SAP HANA, AWS DynamoDB, and AWS RDS — as workflow activities on the platform.",
      "Automated IRMS data migration by integrating Veeva Vault webhooks, removing manual data-entry steps.",
    ],
    impact:
      "Eliminated manual migration effort and expanded the platform's integration coverage across enterprise data sources.",
    stack: [
      "C#",
      ".NET Core",
      "SQL Server",
      "AWS DynamoDB",
      "AWS RDS",
      "SAP HANA",
      "Redis",
    ],
  },
  {
    company: "WRTeam",
    role: "SDE Intern",
    period: "Dec 2022 — Apr 2023",
    location: "Bhuj, India",
    current: false,
    summary:
      "Built the customer experience for a multi-vendor grocery platform.",
    problem:
      "A multi-vendor grocery app needed a smooth, responsive customer panel with real-time order visibility.",
    approach: [
      "Designed and developed a responsive customer-facing panel with React and Redux.",
      "Implemented cart management with item updates, price calculation, and vendor-based grouping.",
      "Delivered real-time order tracking so customers could monitor delivery progress live.",
    ],
    impact:
      "Improved customer satisfaction with live tracking and a friction-free shopping flow.",
    stack: ["JavaScript", "React", "Redux", "Node.js", "PostgreSQL"],
  },
];

export const projects = [
  {
    name: "CineReserve",
    tagline: "Real-time movie ticket booking system",
    featured: true,
    problem:
      "Booking systems must prevent double-booked seats under concurrent load while staying responsive.",
    solution:
      "A full-stack booking platform with real-time seat reservation, show scheduling, snack ordering, and an admin panel.",
    highlight:
      "Secured with JWT, refresh tokens, and OTP; used MySQL and MongoDB together to match each data shape to the right store.",
    stack: ["React", "Express", "MongoDB", "MySQL", "JWT", "OTP"],
  },
  {
    name: "GoalTrackr",
    tagline: "A focused Jira alternative for agile teams",
    featured: true,
    problem:
      "Small teams wanted agile planning without the weight and cost of enterprise tools.",
    solution:
      "A lightweight project tracker with sprints, boards, backlogs, issue types, and role-based access — built for speed and clarity.",
    highlight:
      "Designed the board and sprint model around fast, keyboard-first interactions and clean data ownership per workspace.",
    stack: ["TypeScript", "React", "Node.js", "PostgreSQL", "RBAC"],
  },
  {
    name: "eGrocer",
    tagline: "Multi-vendor grocery ordering panel",
    featured: false,
    problem:
      "Shoppers needed a smooth experience across multiple vendors in one cart.",
    solution:
      "A responsive customer panel with product browsing, vendor-grouped cart management, and real-time order tracking.",
    highlight:
      "A modular Redux architecture enabled ~40% faster feature delivery.",
    stack: ["React", "Redux", "Node.js", "PostgreSQL"],
  },
  {
    name: "CommunityGrid",
    tagline: "Serverless community platform for a real client",
    featured: false,
    problem:
      "A community needed a fast, SEO-friendly site with near-zero hosting and maintenance overhead.",
    solution:
      "A fully responsive site using Google Sheets as a backend and Firebase Hosting, with automated workflows and auth.",
    highlight:
      "A pragmatic serverless approach — shipping real value without infrastructure the client couldn't maintain.",
    stack: ["Firebase", "Google Sheets API", "SEO", "Serverless"],
  },
  {
    name: "RechargeX",
    tagline: "Online mobile recharge platform",
    featured: false,
    problem:
      "Users needed reliable prepaid recharges with transparent transaction history.",
    solution:
      "A recharge system with secure auth, account management, and real-time recharge status updates.",
    highlight:
      "Real-time status updates and a clean transaction ledger for trust.",
    stack: ["React", "Node.js", "REST APIs", "Auth"],
  },
  {
    name: "MailClone",
    tagline: "A Gmail-inspired email client",
    featured: false,
    problem:
      "A practice ground for building the full lifecycle of an email product.",
    solution:
      "Send/receive, image sharing, search, drafts, and sent-mail management in a familiar interface.",
    highlight:
      "Modeled folders, drafts, and search around a clean, extensible data layer.",
    stack: ["React", "Node.js", "MongoDB"],
  },
];

export const skillGroups = [
  {
    title: "Backend",
    description:
      "The service layer businesses run on — APIs, domain logic, and data models designed to stay clean as they scale.",
    items: [
      "Laravel",
      "Node.js",
      "Express",
      ".NET Core",
      "PHP",
      "Go",
      "Python",
      "C#",
    ],
  },
  {
    title: "Identity & Access",
    description:
      "Company-wide SSO, authorization, and access governance — the security-critical backbone multiple products depend on.",
    items: [
      "SSO",
      "Identity Center / IAM",
      "RBAC",
      "Token Management",
      "Multi-Tenant Authorization",
      "Feature Entitlement",
    ],
  },
  {
    title: "Payments & FinTech",
    description:
      "Billing, subscriptions, and payment processing where correctness on money-movement paths is non-negotiable.",
    items: [
      "Billing & Invoicing",
      "Subscriptions / Entitlements",
      "Payment Processing",
      "Recurring Automation",
    ],
  },
  {
    title: "Architecture",
    description:
      "Multi-tenant SaaS, microservices, and Multi-SPA integration with clean boundaries that let teams move in parallel.",
    items: [
      "Multi-Tenant SaaS",
      "Microservices",
      "Multi-SPA",
      "Domain-Driven Design",
      "API Design",
    ],
  },
  {
    title: "Cloud & DevOps",
    description:
      "Infrastructure as code and CI/CD pipelines that make zero-downtime deploys routine rather than risky.",
    items: [
      "AWS",
      "CloudFormation (IaC)",
      "Docker",
      "CodePipeline / CodeBuild",
      "CI/CD",
    ],
  },
  {
    title: "Databases",
    description:
      "Choosing and tuning the right store for each workload — relational for integrity, document and cache where they fit.",
    items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Prisma ORM"],
  },
  {
    title: "Frontend",
    description:
      "Responsive, accessible product UIs that make complex systems feel simple.",
    items: ["React", "Redux", "Next.js", "TypeScript"],
  },
  {
    title: "Observability & Analytics",
    description:
      "Tracing, metrics, and product analytics so problems — and drop-offs — are caught before they cost.",
    items: ["Datadog", "OpenTelemetry", "OpenSearch"],
  },
];

export const timeline = [
  {
    year: "2025 — Now",
    title: "Software Engineering Team Leader",
    subtitle: "Mocha Technologies · Remote",
    detail: "Leading backend security & identity across multiple products.",
  },
  {
    year: "2024 — 2025",
    title: "Software Engineer I",
    subtitle: "Mocha Technologies · Remote",
    detail: "Billing, subscriptions & payments for Mocha Accounting.",
  },
  {
    year: "2023 — 2024",
    title: "Junior Software Engineer",
    subtitle: "Confidosoft Solutions · Vadodara",
    detail: "Integration connectors & data-migration automation.",
  },
  {
    year: "2022 — 2023",
    title: "SDE Intern",
    subtitle: "WRTeam · Bhuj",
    detail: "Customer experience for a multi-vendor grocery platform.",
  },
  {
    year: "2019 — 2023",
    title: "B.Tech, Computer Engineering",
    subtitle: "Dharmsinh Desai Institute of Technology · CGPA 8.52",
    detail: "AWS Academy Graduate (2022) · ICPC Asia Amritapuri (2021).",
  },
];

export const navLinks = [
  { label: "About", href: "#about" },
  { label: "Work", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Skills", href: "#skills" },
  { label: "Code", href: "#opensource" },
  { label: "Path", href: "#timeline" },
];
